import {
  type Capability,
  type Discipline,
  type EngineFamily,
  type ProjectPhase,
  type ProjectProfile,
  type WorkItemType,
} from '../capabilities/catalog.ts';
import type { Vendor } from '../vendors.ts';
import type { WorkCategory } from '../work-category.ts';

/** The only executable roles in the current pipeline. Planning is a Refining activity and
 * testing is verification/CI, so neither gets a separate agent route. */
export const EXECUTION_ROLES = ['refiner', 'implementer', 'reviewer'] as const;
export type ExecutionRole = (typeof EXECUTION_ROLES)[number];

/**
 * Thinking levels a route may name. Wider than the four Nightshift used to offer because the
 * providers report more: Codex advertises `max` and `ultra` beside the first four. The runner
 * passes the value through to the CLI, so the enum tracks what the sources report rather than
 * mapping unfamiliar levels down onto `xhigh`.
 */
export const THINKING_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] as const;
export type ThinkingLevel = (typeof THINKING_LEVELS)[number];

/** One ordered fallback entry. The harness and model are deliberately stored together: a model
 * name is not globally meaningful when more than one CLI can provide it. */
export interface ExecutionRoute {
  harness: Vendor;
  model: string;
  thinking: ThinkingLevel | null;
}

/**
 * One role's lists at one scope. `routes` is the standard list. `light_routes` is the optional
 * cheaper list a small ticket's first attempt of the stage tries before escalating to
 * `routes` (`core/src/pipeline/sized-routing.ts`). A table may name only `light_routes`, in
 * which case `routes` is empty and the standard list is inherited from the next layer down.
 */
export interface ExecutionRoleSection {
  routes: ExecutionRoute[];
  light_routes?: ExecutionRoute[];
  /**
   * The settings editor's row order for `routes` across the general list and every machine
   * section: one entry per row, the machine's name or null for the general list. Only the
   * general table (`execution.<role>`) carries it. Routing reads each list on its own, so this
   * orders the editor and nothing else; absent, the general rows come first.
   */
  order?: (string | null)[];
  /** The same for `light_routes`. */
  light_order?: (string | null)[];
}

type ExecutionMachineSection = Partial<Record<ExecutionRole, ExecutionRoleSection>>;

export interface ExecutionSection {
  /** Opt in after compatible runners are deployed; pinned per attempt. */
  implementer_report_version?: 1;
  refiner: ExecutionRoleSection;
  implementer: ExecutionRoleSection;
  reviewer: ExecutionRoleSection;
  machines: Record<string, ExecutionMachineSection>;
}

/**
 * One CI job that produces objective verification proof for one or more requirements.
 *
 * The project owns the command: GitLab CI runs `job`, the project's own script decides what
 * launching and capturing mean, and Nightshift only learns which requirement names that job
 * claims to satisfy and where to read its machine-readable result. Deliberately no second
 * editable copy of a shell command lives here: the project's script is the only definition.
 */
export interface VerificationProducer {
  /** Exact GitLab job name. No wildcards or expressions: the observer matches it literally. */
  job: string;
  /**
   * Requirement names (`launch`, `capture`, …) this job proves.
   *
   * Two producers may share one only by partitioning it with `scenarios`. A bare `provides`
   * claims the whole requirement and nothing else may also claim it.
   */
  provides: string[];
  /**
   * The scenario names this job supplies for the requirements it provides. Absent means all
   * of them.
   *
   * CELL-29 parked repeatedly off green CI because Cell's `verify:runtime` declared the broad
   * `capture` requirement while the three scenarios the refiner asked for could only come from
   * the visual job -- and there was no way to say so, because the second producer could not
   * declare `capture` too. This is that sentence. It also lets the freeze refuse a scenario no
   * declared job can produce, instead of two pipelines discovering it afterwards.
   */
  scenarios?: string[];
  /** Repository-relative path of the JSON result artifact the job uploads. */
  result: string;
}

/**
 * `verification.retry` — how patient verification is with a runner that is not cooperating.
 *
 * Every field is optional and falls back to `DEFAULT_VERIFICATION_RETRY_POLICY`. Configured
 * beside the producers it bounds: how many times a session is worth asking again is a claim
 * about this project's CI.
 */
export interface VerificationRetrySection {
  /** Retries of the required job Nightshift itself asks for, per head, after a blocked outcome. */
  max_job_retries?: number;
  /** Failed reads of the result or its proof, at one head and pipeline, before giving up. */
  read_attempts?: number;
  /** How long a required job may stay queued or running before the wait becomes a park. */
  pending_wait_ms?: number;
}

/**
 * Accepted range for each `verification.retry` field, keyed by its stored name.
 *
 * Bounded on both sides. An unbounded retry count is a retry storm with a configuration file
 * in front of it, and an unbounded pending wait is indefinite silent waiting.
 * `max_job_retries` alone may be zero: that switches automatic
 * retries off while leaving the operator's Retry verification action working.
 */
export const VERIFICATION_RETRY_LIMITS = Object.freeze({
  max_job_retries: Object.freeze({ min: 0, max: 10 }),
  read_attempts: Object.freeze({ min: 1, max: 50 }),
  pending_wait_ms: Object.freeze({ min: 60_000, max: 12 * 60 * 60 * 1000 }),
});

/**
 * `verification.baselines` — where the project keeps the expected images its visual harness
 * compares against, so Nightshift can publish an approved actual image to the right path.
 *
 * Nightshift never decides what "different" means; the project's harness does. This only says
 * where a scenario's baseline lives and how the harness names the actual and difference files
 * it uploads, so an operator's approval can be carried out mechanically.
 */
export interface VerificationBaselinesSection {
  /** Repository directory holding the expected images, forward slashes, no trailing slash. */
  dir: string;
  /** Filename pattern per scenario; `{scenario}` is replaced. Default `{scenario}.png`. */
  file?: string;
  /** Substring that marks the harness's actual image among a failed check's artifacts. Default `actual`. */
  actual_marker?: string;
  /** Substring that marks the difference image. Default `diff`. */
  diff_marker?: string;
  /**
   * Refuse to merge a branch that changes a file under `dir` with no recorded approval.
   * Default true.
   *
   * A project that declares where its baselines live has said those images are the standard
   * its work is judged against, and a branch that rewrites the standard it is about to be
   * judged against passes itself. CELL-23 reached a green pipeline that way.
   */
  require_approval?: boolean;
}

/** `verification` — producer declarations keyed by a local name (`producers.runtime`). */
export interface VerificationSection {
  producers: Record<string, VerificationProducer>;
  /** Bounds on automatic recovery. Absent means the built-in defaults. */
  retry?: VerificationRetrySection;
  /** Where approved baseline images are published. Absent means baseline updates are not offered. */
  baselines?: VerificationBaselinesSection;
}

/**
 * `artifacts.<name>` -- one build output the board should put a link to on the ticket card.
 *
 * GitLab lists what a job uploaded only as one archive per job; nothing in its API says which
 * file inside that archive is the thing a person wants. The project says so here, in the same
 * words its `.gitlab-ci.yml` uses for `artifacts:expose_as`: the job, and the path inside its
 * upload. A declaration without a `path` names the whole archive.
 */
export interface ArtifactDeclaration {
  /** Exact GitLab job name, matched literally like a verification producer's. */
  job: string;
  /** Repository-relative path inside the job's upload. Absent means the job's archive. */
  path?: string;
  /** What the card calls it (`Android APK`). */
  label: string;
}

/** `artifacts` -- declarations keyed by a local name (`android`). */
export type ArtifactsSection = Record<string, ArtifactDeclaration>;

/** Hosts a project can name in `environment`; a runner's `os` maps onto one of these. */
export const ENVIRONMENT_HOSTS = ['windows', 'macos', 'linux'] as const;
export type EnvironmentHost = (typeof ENVIRONMENT_HOSTS)[number];

/** Operations a role may need locally. `build` and `run` are the only ones a host can refuse. */
export const ENVIRONMENT_OPERATIONS = ['build', 'run'] as const;
export type EnvironmentOperation = (typeof ENVIRONMENT_OPERATIONS)[number];

/**
 * `environment`: what the repository needs from the machine that works on it.
 *
 * Explicit requirements only. Nothing here is inferred from `package.json` or a project file:
 * a project that declares nothing keeps today's behaviour, and a project configured for
 * PowerShell is never blocked for lacking an npm gate.
 */
export interface EnvironmentSection {
  /** Android USB device digest, or any authorised attached device. Required for run operations only. */
  android_device?: string;
  /** Hosts the project supports building on. Empty means any host. */
  build_hosts: EnvironmentHost[];
  /** Hosts the project supports launching on. Empty means the build hosts. */
  run_hosts: EnvironmentHost[];
  /** Toolchain ids every build or run needs on the machine (`dotnet-sdk`, `node`, `pwsh`, ...). */
  tools: string[];
  /** Shell the commands run under (`pwsh`, `powershell`, `bash`, `sh`, `cmd`). Empty means the platform default. */
  shell: string;
  /** Directory the commands run in, relative to the repository root. Empty means the root. */
  working_dir: string;
  /** True when running the project needs an interactive graphics session (a window, a GPU). */
  graphics_session: boolean;
  /**
   * True when running the project needs to post synthetic input into that window.
   *
   * Separate from `graphics_session` because the permissions are separate: the Mac that ran
   * CELL-60 could open a window and screenshot it all night, and dropped every injected click
   * because the runner account held no Accessibility right. A screenshot test needs the first;
   * a gameplay test needs both.
   */
  input_session: boolean;
  /** Operations the implementer performs locally. Default `build`. */
  implementer_operations: EnvironmentOperation[];
  /** Operations the reviewer performs locally. Default none: a diff-and-CI reviewer runs anywhere. */
  reviewer_operations: EnvironmentOperation[];
}

/**
 * Leftover v0.12 `review.max_cycles`. Per-ticket `tickets.budgets_json`
 * (named 5-return families) is canonical and must drive repair decisions;
 * this value must not. It remains the displayed cycle cap.
 */
export const DEFAULT_CYCLE_CAP = 6;

export interface ProjectSection {
  /** Ticket-key prefix Nightshift mints with. Empty falls back to the project slug. */
  tracker_project: string;
  /**
   * Capability tags every stage of this project requires. Stage extras in
   * `requiredTags` are unioned on top. Empty means no tag gate.
   */
  required_tags: string[];
}

/**
 * Named defaults-and-arrangement layer over capabilities (#1, absorbing #3's phase).
 * A `null` phase follows the profile's arrangement.
 */
export interface ProfileSection {
  kind: ProjectProfile;
  phase: ProjectPhase | null;
}

/** Explicit capability switches. An absent capability follows the profile arrangement (#7). */
export type CapabilitiesSection = Partial<Record<Capability, boolean>>;

/** Engine / toolchain identity consumed by prompts and builds (#2). */
export interface EngineSection {
  family: EngineFamily;
  name: string;
  version: string;
}

/**
 * Which vocabularies this project uses. The values themselves live on tracker labels;
 * this list only says which ones grouping and the wizard offer (#4, #5). `null` follows the
 * profile's arrangement; an empty list is an explicit "none".
 */
export interface TaxonomySection {
  disciplines: Discipline[] | null;
  work_item_types: WorkItemType[] | null;
}

/** Commands documented for agents and pinned into the environment contract. Empty means unset. */
export interface CommandsSection {
  build: string;
  test: string;
}

/**
 * Extra capability tags per stage, unioned with `project.required_tags`.
 *
 * An empty list adds nothing. These are requirements the claiming runner must advertise,
 * not a second pin: jobs are still targeted by runner name.
 */
export interface RequiredTagsSection {
  refine: string[];
  plan: string[];
  implement: string[];
  test: string[];
  review: string[];
}

/**
 * Which review findings a person sees. Only `error` findings can fail a review; `warning` and
 * `info` ride along on a pass. This filters what is shown, never what the reviewer may block on.
 */
export const REVIEW_FINDINGS_SHOWN = ['errors', 'warnings', 'all'] as const;
export type ReviewFindingsShown = (typeof REVIEW_FINDINGS_SHOWN)[number];

export function isReviewFindingsShown(value: unknown): value is ReviewFindingsShown {
  return typeof value === 'string' && (REVIEW_FINDINGS_SHOWN as readonly string[]).includes(value);
}

export interface ReviewSection {
  /**
   * Risk-tiered auto-merge. Off by default and opt-in per project: when on, a ticket whose
   * first AI Review wave passes is approved by policy instead of waiting in Human Review, but
   * only if every condition in `evaluateAutoMergePolicy` holds. Anything else still goes to a
   * person exactly as before.
   */
  auto_merge: boolean;
  /** Largest diff, added plus removed lines at the reviewed head, that policy may approve. */
  auto_merge_max_changed_lines: number;
  /** Ticket categories (the effective work category) policy may approve. */
  auto_merge_categories: WorkCategory[];
  max_cycles: number;
  /** Lowest finding severity shown on the ticket: blockers only, plus warnings, or everything. */
  findings_shown: ReviewFindingsShown;
}

/**
 * The auto-merge defaults.
 *
 * 200 lines is roughly the size of change a person reviews in one sitting without skimming,
 * and it is where the production median diff sits for tickets that passed review first time.
 * `hygiene` alone is the conservative category default: tidy-ups, docs, tests and small
 * refactors carry the least product risk. Everything else, `unclassified` in particular, waits
 * for a person until the owner widens the list.
 */
const DEFAULT_AUTO_MERGE_MAX_CHANGED_LINES = 200;
const DEFAULT_AUTO_MERGE_CATEGORIES: readonly WorkCategory[] = ['hygiene'];

/**
 * What a refiner's `obsolete` claim may do. `propose` (the default) parks every claim for a
 * person. `auto_cancel` lets a high-confidence claim on a ticket that never had a merge request
 * and that nothing unfinished depends on go to an independent check by another vendor, and
 * cancels the ticket by policy when that check confirms it. Restore undoes the cancellation.
 */
export const OBSOLETE_TICKET_POLICIES = ['propose', 'auto_cancel'] as const;
export type ObsoleteTicketPolicy = (typeof OBSOLETE_TICKET_POLICIES)[number];

export interface RefineSection {
  obsolete_tickets: ObsoleteTicketPolicy;
}

/** Optional project-specific direction inserted without changing a stage's immutable contract. */
export interface GuidanceSection {
  refine: string;
  plan: string;
  implement: string;
  review: string;
}

/** Keep one editable section useful without letting it consume an agent's whole prompt budget. */
export const MAX_STAGE_GUIDANCE_BYTES = 16 * 1024;

/**
 * The version of the stored project-configuration document. A reader that finds a newer
 * version than it knows refuses it rather than guessing at fields it cannot interpret.
 */
export const PROJECT_CONFIGURATION_SCHEMA_VERSION = 1;

/**
 * One project's complete configuration: the single document Nightshift stores per project and
 * every consumer reads. There are no layers beneath it and nothing outside the database is
 * consulted; a project that has never been configured reads the built-in defaults below.
 */
export interface ProjectConfiguration {
  project: ProjectSection;
  requiredTags: RequiredTagsSection;
  commands: CommandsSection;
  guidance: GuidanceSection;
  review: ReviewSection;
  refine: RefineSection;
  profile: ProfileSection;
  capabilities: CapabilitiesSection;
  engine: EngineSection;
  taxonomy: TaxonomySection;
  /** Ordered model routes per role, and per-machine replacements. Empty lists inherit Nightshift → Models. */
  execution: ExecutionSection;
  /**
   * Which CI jobs produce verification proof. `null` is the explicitly identified legacy path
   * that matches evidence by job name.
   */
  verification: VerificationSection | null;
  /** What the machine that works on the repository must be. `null` constrains nothing. */
  environment: EnvironmentSection | null;
  /** Which CI uploads the board links from the ticket card. Empty singles nothing out. */
  artifacts: ArtifactsSection;
}

/** The documented built-in defaults a project runs on until someone configures it. */
export function defaultProjectConfiguration(): ProjectConfiguration {
  return {
    project: { tracker_project: '', required_tags: [] },
    requiredTags: { refine: [], plan: [], implement: [], test: [], review: [] },
    commands: { build: '', test: '' },
    guidance: { refine: '', plan: '', implement: '', review: '' },
    review: {
      auto_merge: false,
      auto_merge_max_changed_lines: DEFAULT_AUTO_MERGE_MAX_CHANGED_LINES,
      auto_merge_categories: [...DEFAULT_AUTO_MERGE_CATEGORIES],
      max_cycles: DEFAULT_CYCLE_CAP,
      findings_shown: 'all',
    },
    refine: { obsolete_tickets: 'propose' },
    // software + no explicit capabilities: arrangeProject lights budgets from the profile.
    profile: { kind: 'software', phase: null },
    capabilities: {},
    engine: { family: 'none', name: '', version: '' },
    taxonomy: { disciplines: null, work_item_types: null },
    execution: { refiner: { routes: [] }, implementer: { routes: [] }, reviewer: { routes: [] }, machines: {} },
    verification: null,
    environment: null,
    artifacts: {},
  };
}
